// SalesInfoStep component - matches product-merchant-crud.html Step 3 exactly
// Simple sales configuration, variant management, and pricing
// Based on product-merchant-crud.html sales info section

import React, { useCallback, useState, useEffect } from "react";
import { FormField } from "src/shared/forms/components";
// Removed VariantOptionsBuilder - implementing custom variant management to match HTML design exactly
// Complex variant structures matching HTML design
// Variant value object structure matching HTML design exactly
export interface VariantValue {
  name: string;
  image?: string; // Only for first option (like Color)
}

export interface VariantOption {
  id: string;
  name: string; // Color, Size, Material, etc.
  index: number;
  values: VariantValue[]; // [{ name: "Red", image: "..." }, { name: "Blue" }]
}

export interface VariantCombination {
  id: string;
  name: string; // "Red - Large"
  options: string[]; // ["Color", "Size"]
  optionValues: string[]; // ["Red", "Large"]
  tierIndex?: number[]; // Index position in each option's values array for HTML compatibility
  image?: string;
  price: number;
  cost?: number;
  stock: number;
  sku: string;
  warehouseStock: { [warehouseCode: string]: number };
  isActive: boolean;
  isMainVariant?: boolean;
}

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Simplified interface matching HTML design exactly
export interface SalesInfoData {
  // Minimum Purchase (always visible)
  minOrder: number;

  // Product variants toggle
  hasVariants: boolean;

  // Base pricing (when no variants)
  productPrice?: number;
  salePrice?: number;
  costPrice?: number;
  currency?: string;

  // Stock management (when no variants)
  totalStock?: number;
  warehouseStock?: { [key: string]: number };

  // Variant management (when variants enabled)
  variantOptions?: VariantOption[];
  variantCombinations?: VariantCombination[];

  // Product tags and promotion
  tags?: string[];
  promotionTags?: string[];
}

// Sales metrics interface for analytics display
export interface SalesMetrics {
  salesQty30d: number;
  salesRevenue30d: number;
  avgDailySalesQty30d: number;
  salesQty100d: number;
  salesRevenue100d: number;
  avgDailySalesQty100d: number;
  salesVelocity: number;
  lastOrderDate?: string;
}

// Pricing metrics interface
export interface PricingMetrics {
  averageSellingPrice: number;
  averageMargin: number;
  averageDiscountPercent: number;
}

// Warehouse data matching HTML design
const WAREHOUSES = [
  { id: "A", code: "A", name: "Warehouse A", location: "Jakarta" },
  { id: "B", code: "B", name: "Warehouse B", location: "Surabaya" },
  { id: "C", code: "C", name: "Warehouse C", location: "Bandung" },
];

// Variant Value Input Component - matching HTML design exactly
interface VariantValueInputProps {
  optionIndex: number;
  option: VariantOption;
  onAddValue: (optionIndex: number, value: string) => void;
  getVariantSuggestions: (optionName: string) => string[];
}

function VariantValueInput({
  optionIndex,
  option,
  onAddValue,
  getVariantSuggestions,
}: VariantValueInputProps) {
  const [newValue, setNewValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = React.useRef<HTMLDivElement>(null);

  const handleAddValue = useCallback(() => {
    if (newValue.trim()) {
      onAddValue(optionIndex, newValue.trim());
      setNewValue("");
      setShowSuggestions(false);
    }
  }, [newValue, optionIndex, onAddValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddValue();
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [handleAddValue]
  );

  // Click outside handler to close suggestions
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSuggestions]);

  const availableSuggestions = getVariantSuggestions(option.name).filter(
    (s) =>
      !option.values.find((v) => v.name === s) &&
      s.toLowerCase().includes(newValue.toLowerCase())
  );

  return (
    <div className="tw-relative" ref={inputRef}>
      <input
        value={newValue}
        onChange={(e) => setNewValue(e.target.value)}
        onInput={() => setShowSuggestions(true)}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        className="tw-w-full tw-bg-white dark:tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
        placeholder={`Add ${option.name || "option"} value...`}
        type="text"
      />
      <button
        onClick={handleAddValue}
        className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-primary hover:tw-text-primary/80 tw-transition-colors"
        type="button"
      >
        <span className="material-symbols-outlined tw-text-[20px]">add</span>
      </button>

      {/* Suggestions dropdown */}
      {showSuggestions && getVariantSuggestions(option.name).length > 0 && (
        <div
          className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-shadow-lg tw-z-20 tw-max-h-48 tw-overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tw-p-2 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
            Common {option.name} values:
          </div>
          <div className="tw-py-1">
            {availableSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  onAddValue(optionIndex, suggestion);
                  setNewValue("");
                  setShowSuggestions(false);
                }}
                className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-text-gray-900 dark:tw-text-white tw-flex tw-items-center tw-gap-2"
              >
                {suggestion}
              </button>
            ))}
            {availableSuggestions.length === 0 && (
              <div className="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                No matching suggestions. Press Enter to add "{newValue}".
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export interface SalesInfoStepProps {
  data: SalesInfoData;
  onChange: (data: SalesInfoData) => void;
  errors?: string[];
  isActive?: boolean;
  onValidate?: () => string[];
}

const SalesInfoStep: React.FC<SalesInfoStepProps> = ({
  data: salesInfoData,
  onChange,
  errors = [],
}) => {
  // Get error for specific field - improved matching
  const getFieldError = (fieldName: string): string | undefined => {
    const fieldMap: { [key: string]: string[] } = {
      minOrder: ["Minimum order", "minimum"],
      productPrice: ["Regular price", "price"],
      costPrice: ["Cost price", "cost"],
      salePrice: ["Sale price", "sale"],
      variantOptions: ["variant option", "variant"],
      variantCombinations: ["variant"],
    };

    const patterns = fieldMap[fieldName] || [fieldName];
    return errors.find((error) =>
      patterns.some((pattern) =>
        error.toLowerCase().includes(pattern.toLowerCase())
      )
    );
  };
  // State for warehouse stock expansion (matching HTML design)
  const [expandedNonVariantStock, setExpandedNonVariantStock] = useState(false);
  const [variantStockExpanded, setVariantStockExpanded] = useState<{
    [key: number]: boolean;
  }>({});

  // Variant management state - matching HTML design approach
  const [optionDropdownOpen, setOptionDropdownOpen] = useState<{
    [key: number]: boolean;
  }>({});

  // Product tags state - matching HTML design
  const [currentTag, setCurrentTag] = useState("");
  const [currentPromotionTag, setCurrentPromotionTag] = useState("");

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOptionDropdownOpen({});
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Variant option suggestions matching HTML design exactly
  const getVariantSuggestions = (optionName: string): string[] => {
    const suggestions = {
      Color: [
        "Red",
        "Blue",
        "Green",
        "Yellow",
        "Black",
        "White",
        "Gray",
        "Pink",
        "Purple",
        "Orange",
        "Brown",
        "Navy",
        "Teal",
      ],
      Size: [
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "XXL",
        "XXXL",
        "28",
        "30",
        "32",
        "34",
        "36",
        "38",
        "40",
        "42",
      ],
      Material: [
        "Cotton",
        "Polyester",
        "Silk",
        "Wool",
        "Linen",
        "Leather",
        "Denim",
        "Canvas",
        "Velvet",
        "Satin",
      ],
      Style: [
        "Casual",
        "Formal",
        "Sport",
        "Classic",
        "Modern",
        "Vintage",
        "Minimalist",
        "Bohemian",
      ],
      Pattern: [
        "Solid",
        "Striped",
        "Polka Dot",
        "Floral",
        "Geometric",
        "Abstract",
        "Plaid",
        "Leopard",
      ],
      Flavor: [
        "Vanilla",
        "Chocolate",
        "Strawberry",
        "Mint",
        "Coffee",
        "Caramel",
        "Lemon",
        "Orange",
      ],
      Weight: ["100g", "250g", "500g", "1kg", "2kg", "5kg", "10kg"],
      Length: ["30cm", "50cm", "1m", "1.5m", "2m", "3m", "5m"],
      Width: ["10cm", "20cm", "30cm", "50cm", "100cm", "150cm"],
      Capacity: ["100ml", "250ml", "500ml", "1L", "2L", "5L", "10L"],
    };
    return suggestions[optionName as keyof typeof suggestions] || [];
  };

  // Handle field changes - simplified for better React state updates
  const handleFieldChange = useCallback(
    (field: keyof SalesInfoData, value: any) => {
      // Create new data object ensuring all required fields exist
      const newData: SalesInfoData = {
        ...salesInfoData,
        [field]: value,
        // Only set defaults for fields that aren't being updated
        minOrder: field === "minOrder" ? value : salesInfoData.minOrder || 1,
        hasVariants:
          field === "hasVariants" ? value : salesInfoData.hasVariants || false,
        variantOptions:
          field === "variantOptions"
            ? value
            : salesInfoData.variantOptions || [],
        variantCombinations:
          field === "variantCombinations"
            ? value
            : salesInfoData.variantCombinations || [],
        warehouseStock:
          field === "warehouseStock"
            ? value
            : salesInfoData.warehouseStock || {},
      };

      onChange(newData);
    },
    [salesInfoData, onChange]
  );

  // Handle total stock changes with proper distribution across warehouses
  const handleTotalStockChange = useCallback(
    (newTotalStock: number) => {
      const warehouseCount = WAREHOUSES.length;
      const baseAmount = Math.floor(newTotalStock / warehouseCount);
      const remainder = newTotalStock % warehouseCount;

      const updatedWarehouseStock: { [key: string]: number } = {};
      WAREHOUSES.forEach((warehouse, index) => {
        updatedWarehouseStock[warehouse.code] = baseAmount + (index < remainder ? 1 : 0);
      });

      const newData = {
        ...salesInfoData,
        totalStock: newTotalStock,
        warehouseStock: updatedWarehouseStock,
      };
      onChange(newData);
    },
    [salesInfoData, onChange]
  );

  // Handle warehouse stock changes for non-variant products
  const handleWarehouseStockChange = useCallback(
    (warehouseId: string, stock: number) => {
      const currentWarehouseStock = salesInfoData.warehouseStock || {};
      const updatedWarehouseStock = {
        ...currentWarehouseStock,
        [warehouseId]: stock,
      };

      // Calculate total stock from warehouse stocks
      const totalStock = Object.values(updatedWarehouseStock).reduce(
        (sum, stock) => sum + (Number(stock) || 0),
        0
      );

      // Update both warehouseStock and totalStock atomically to avoid validation errors
      const newData = {
        ...salesInfoData,
        warehouseStock: updatedWarehouseStock,
        totalStock: totalStock,
      };
      onChange(newData);
    },
    [salesInfoData, onChange]
  );


  // Helper function to calculate combinations from options (FIXED VERSION)
  const calculateVariantCombinations = useCallback(
    (options: VariantOption[], currentData: SalesInfoData) => {
      const filledOptions = options.filter(
        (opt) => opt.name && opt.values.length > 0
      );

      if (filledOptions.length === 0) {
        return [];
      }

      // Generate all possible combinations
      const combinations: number[][] = [];
      const generateCombos = (index: number, current: number[]) => {
        if (index === filledOptions.length) {
          combinations.push([...current]);
          return;
        }

        for (let i = 0; i < filledOptions[index].values.length; i++) {
          current.push(i);
          generateCombos(index + 1, current);
          current.pop();
        }
      };

      generateCombos(0, []);

      // Create variant combination objects
      const variantCombinations = combinations.map((combo, idx) => {
        const name = combo
          .map(
            (valueIdx: number, optIdx: number) =>
              filledOptions[optIdx].values[valueIdx].name
          )
          .join(" - ");

        // Check if combination already exists to preserve data - use currentData parameter
        const existing = (currentData.variantCombinations || []).find(
          (vc) => JSON.stringify(vc.tierIndex) === JSON.stringify(combo)
        );

        return (
          existing || {
            id: "combo_" + Date.now() + "_" + idx,
            tierIndex: combo,
            name: name,
            sku: "",
            stock: 0,
            price: currentData.productPrice || 0,
            cost: currentData.costPrice || 0,
            options: filledOptions.map((opt) => opt.name),
            optionValues: combo.map(
              (valueIdx: number, optIdx: number) =>
                filledOptions[optIdx].values[valueIdx].name
            ),
            warehouseStock: { A: 0, B: 0, C: 0 },
            isActive: true,
            isMainVariant: idx === 0,
          }
        );
      });

      return variantCombinations;
    },
    []
  );

  // Variant combination update function - FIXED VERSION
  const updateVariantCombinations = useCallback(
    (options: VariantOption[]) => {
      // Get current data to avoid stale closures
      const currentData = salesInfoData;
      const newCombinations = calculateVariantCombinations(
        options,
        currentData
      );
      handleFieldChange("variantCombinations", newCombinations);
    },
    [salesInfoData, handleFieldChange, calculateVariantCombinations]
  );

  // Variant management functions matching HTML design
  const addVariantOption = useCallback(() => {
    const currentOptions = salesInfoData.variantOptions || [];
    const newIndex = currentOptions.length;
    const newOption: VariantOption = {
      id: "option_" + Date.now(),
      name: "",
      index: newIndex,
      values: [],
    };

    const updatedOptions = [...currentOptions, newOption];

    // Update state directly with complete data object
    const newData: SalesInfoData = {
      ...salesInfoData,
      minOrder: salesInfoData.minOrder || 1,
      hasVariants: salesInfoData.hasVariants || false,
      variantOptions: updatedOptions,
      variantCombinations: salesInfoData.variantCombinations || [],
      warehouseStock: salesInfoData.warehouseStock || {},
    };

    onChange(newData);

    // Initialize dropdown state
    setOptionDropdownOpen((prev) => ({ ...prev, [newIndex]: false }));
  }, [salesInfoData, onChange]);

  const removeVariantOption = useCallback(
    (optionIndex: number) => {
      const currentOptions = salesInfoData.variantOptions || [];
      const updatedOptions = currentOptions.filter(
        (_, index) => index !== optionIndex
      );
      handleFieldChange("variantOptions", updatedOptions);
      updateVariantCombinations(updatedOptions);
    },
    [salesInfoData.variantOptions, handleFieldChange, updateVariantCombinations]
  );

  const updateVariantOption = useCallback(
    (optionIndex: number, field: keyof VariantOption, value: any) => {
      const currentOptions = salesInfoData.variantOptions || [];
      const updatedOptions = [...currentOptions];

      // Ensure the option exists before updating
      if (updatedOptions[optionIndex]) {
        updatedOptions[optionIndex] = {
          ...updatedOptions[optionIndex],
          [field]: value,
        };

        // Always recalculate combinations when options are updated
        // This ensures combinations are generated/updated whenever options change
        const newCombinations = calculateVariantCombinations(
          updatedOptions,
          salesInfoData
        );

        // Update state with both options and combinations
        const newData: SalesInfoData = {
          ...salesInfoData,
          minOrder: salesInfoData.minOrder || 1,
          hasVariants: salesInfoData.hasVariants || false,
          variantOptions: updatedOptions,
          variantCombinations: newCombinations,
          warehouseStock: salesInfoData.warehouseStock || {},
        };

        onChange(newData);
      }
    },
    [salesInfoData, onChange, calculateVariantCombinations]
  );

  // Update individual variant combination field
  const updateVariantCombination = useCallback(
    (index: number, field: string, value: any) => {
      const currentCombinations = salesInfoData.variantCombinations || [];
      if (currentCombinations[index]) {
        const updatedCombinations = [...currentCombinations];
        updatedCombinations[index] = {
          ...updatedCombinations[index],
          [field]: value,
        };
        handleFieldChange("variantCombinations", updatedCombinations);
      }
    },
    [salesInfoData.variantCombinations, handleFieldChange]
  );

  // Update warehouse stock for a specific variant combination
  const updateVariantWarehouseStock = useCallback(
    (variantIndex: number, warehouseCode: string, stock: number) => {
      const currentCombinations = salesInfoData.variantCombinations || [];
      if (currentCombinations[variantIndex]) {
        const updatedCombinations = [...currentCombinations];
        const combination = updatedCombinations[variantIndex];

        // Update warehouse stock
        combination.warehouseStock = {
          ...combination.warehouseStock,
          [warehouseCode]: stock,
        };

        // Calculate total stock from all warehouses
        const totalStock = Object.values(combination.warehouseStock).reduce(
          (sum, s) => sum + (Number(s) || 0),
          0
        );
        combination.stock = totalStock;

        handleFieldChange("variantCombinations", updatedCombinations);
      }
    },
    [salesInfoData.variantCombinations, handleFieldChange]
  );

  // Handle variant image upload - COMPLETE IMPLEMENTATION
  const handleVariantImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, variantIndex: number) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file.");
        return;
      }

      // Validate file size (2MB limit for variant images)
      if (file.size > 2 * 1024 * 1024) {
        alert("File is too large. Maximum size is 2MB for variant images.");
        return;
      }

      // Create image preview for variant
      const reader = new FileReader();
      reader.onload = (e) => {
        const currentCombinations = salesInfoData.variantCombinations || [];
        if (currentCombinations[variantIndex]) {
          updateVariantCombination(
            variantIndex,
            "image",
            e.target?.result as string
          );
        }
      };
      reader.readAsDataURL(file);

      // Clear the input
      event.target.value = "";
    },
    [salesInfoData.variantCombinations, updateVariantCombination]
  );

  // Tag management functions - matching HTML design
  const addTag = useCallback(() => {
    if (
      currentTag &&
      currentTag.trim() !== "" &&
      !(salesInfoData.tags || []).includes(currentTag.trim())
    ) {
      const updatedTags = [...(salesInfoData.tags || []), currentTag.trim()];
      handleFieldChange("tags", updatedTags);
      setCurrentTag("");
    }
  }, [currentTag, salesInfoData.tags, handleFieldChange]);

  const removeTag = useCallback(
    (index: number) => {
      const updatedTags = (salesInfoData.tags || []).filter(
        (_, i) => i !== index
      );
      handleFieldChange("tags", updatedTags);
    },
    [salesInfoData.tags, handleFieldChange]
  );

  const addPromotionTag = useCallback(() => {
    if (
      currentPromotionTag &&
      currentPromotionTag.trim() !== "" &&
      !(salesInfoData.promotionTags || []).includes(currentPromotionTag.trim())
    ) {
      const updatedPromotionTags = [
        ...(salesInfoData.promotionTags || []),
        currentPromotionTag.trim(),
      ];
      handleFieldChange("promotionTags", updatedPromotionTags);
      setCurrentPromotionTag("");
    }
  }, [currentPromotionTag, salesInfoData.promotionTags, handleFieldChange]);

  const removePromotionTag = useCallback(
    (index: number) => {
      const updatedPromotionTags = (salesInfoData.promotionTags || []).filter(
        (_, i) => i !== index
      );
      handleFieldChange("promotionTags", updatedPromotionTags);
    },
    [salesInfoData.promotionTags, handleFieldChange]
  );

  // Variant value management functions - matching HTML design exactly
  const addOptionValue = useCallback(
    (optionIndex: number, value: string) => {
      const currentOptions = salesInfoData.variantOptions || [];

      if (currentOptions[optionIndex] && value.trim() !== "") {
        const option = currentOptions[optionIndex];

        // Check if value already exists
        if (!option.values.find((v) => v.name === value.trim())) {
          const newValue: VariantValue = {
            name: value.trim(),
            image: optionIndex === 0 ? undefined : undefined, // First option can have images
          };
          const updatedOptions = [...currentOptions];
          updatedOptions[optionIndex] = {
            ...option,
            values: [...option.values, newValue],
          };

          // Calculate new combinations based on updated options
          const newCombinations = calculateVariantCombinations(
            updatedOptions,
            salesInfoData
          );

          // Update both options and combinations in a single state update
          const newData: SalesInfoData = {
            ...salesInfoData,
            variantOptions: updatedOptions,
            variantCombinations: newCombinations,
            minOrder: salesInfoData.minOrder || 1,
            hasVariants: salesInfoData.hasVariants || false,
            warehouseStock: salesInfoData.warehouseStock || {},
          };

          onChange(newData);
        }
      }
    },
    [salesInfoData, calculateVariantCombinations, onChange]
  );

  const removeVariantValue = useCallback(
    (optionIndex: number, valueIndex: number) => {
      const currentOptions = salesInfoData.variantOptions || [];
      if (currentOptions[optionIndex]) {
        const option = currentOptions[optionIndex];
        const updatedOptions = [...currentOptions];
        updatedOptions[optionIndex] = {
          ...option,
          values: option.values.filter((_, i) => i !== valueIndex),
        };

        // Calculate new combinations based on updated options
        const newCombinations = calculateVariantCombinations(
          updatedOptions,
          salesInfoData
        );

        // Update both options and combinations in a single state update
        const newData: SalesInfoData = {
          ...salesInfoData,
          variantOptions: updatedOptions,
          variantCombinations: newCombinations,
          minOrder: salesInfoData.minOrder || 1,
          hasVariants: salesInfoData.hasVariants || false,
          warehouseStock: salesInfoData.warehouseStock || {},
        };

        onChange(newData);
      }
    },
    [salesInfoData, calculateVariantCombinations, onChange]
  );

  return (
    <div className="tw-max-w-full sm:tw-max-w-3xl lg:tw-max-w-4xl tw-mx-auto tw-space-y-4 md:tw-space-y-6">
      {/* Sales Information Section Header */}
      <div className="tw-bg-surface-light dark:tw-bg-surface-dark tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Sales Information
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Configure product variants and pricing details.
          </p>
        </div>

        <div className="tw-grid tw-gap-6">
          {/* Minimum Purchase (Always visible) */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
            <FormField
              label="Minimum Purchase"
              name="minOrder"
              required
              error={getFieldError("minOrder")}
            >
              <input
                type="number"
                value={salesInfoData.minOrder || 1}
                onChange={(e) =>
                  handleFieldChange("minOrder", parseInt(e.target.value) || 1)
                }
                min={1}
                placeholder="1"
                className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
              />

              {!getFieldError("minOrder") && (
                <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                  Minimum quantity required per order (must be at least 1)
                </p>
              )}
            </FormField>
          </div>

          {/* Has Variants Toggle */}
          <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-bg-gray-50 dark:tw-bg-[#151f28] tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152]">
            <div>
              <h4 className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">
                Product Variants
              </h4>
              <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                Does this product have variants like size, color, or material?
              </p>
            </div>
            <label className="tw-relative tw-inline-flex tw-items-center tw-cursor-pointer">
              <input
                type="checkbox"
                className="tw-sr-only tw-peer"
                checked={Boolean(salesInfoData.hasVariants)}
                onChange={(e) => {
                  const isChecked = e.target.checked;

                  // Update state and clear both options and combinations when toggling off
                  const updatedData = {
                    ...salesInfoData,
                    hasVariants: isChecked,
                    // Clear both options and combinations when toggling off to match HTML behavior
                    variantOptions: isChecked
                      ? salesInfoData.variantOptions || []
                      : [],
                    variantCombinations: isChecked
                      ? salesInfoData.variantCombinations || []
                      : [],
                  };

                  onChange(updatedData);
                }}
              />
              <div className="tw-w-11 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-peer peer-checked:after:tw-translate-x-full peer-checked:after:tw-border-white after:tw-content-[''] after:tw-absolute after:tw-top-[2px] after:tw-left-[2px] after:tw-bg-white after:tw-border-gray-300 after:tw-border after:tw-rounded-full after:tw-h-5 after:tw-w-5 after:tw-transition-all dark:tw-bg-gray-700 peer-checked:tw-bg-primary"></div>
            </label>
          </div>

          {/* Base Pricing (shown when no variants) */}
          {!salesInfoData.hasVariants && (
            <div className="tw-space-y-4 md:tw-space-y-6">
              {/* Currency Selection */}
              <FormField
                label="Currency"
                name="currency"
                required
              >
                <select
                  value={salesInfoData.currency || 'IDR'}
                  onChange={(e) => handleFieldChange('currency', e.target.value)}
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                >
                  <option value="IDR">IDR (Indonesian Rupiah)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="SGD">SGD (Singapore Dollar)</option>
                </select>
                <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                  Select the currency for this product
                </p>
              </FormField>
              
              <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4 md:tw-gap-6">
                <FormField
                  label={`Regular Price (${salesInfoData.currency || 'IDR'})`}
                  name="productPrice"
                  required
                  error={getFieldError("productPrice")}
                >
                <input
                  type="number"
                  value={salesInfoData.productPrice || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "productPrice",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  placeholder="150000"
                  min={0}
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                />

                {!getFieldError("productPrice") && (
                  <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                    Base selling price before any discounts
                  </p>
                )}
              </FormField>

              <div className="tw-flex tw-flex-col tw-gap-2">
                <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                  Sale Price ({salesInfoData.currency || 'IDR'})
                </label>
                <input
                  type="number"
                  value={salesInfoData.salePrice || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "salePrice",
                      parseFloat(e.target.value) || undefined
                    )
                  }
                  placeholder="120000"
                  min={0}
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                />
                <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                  Optional discounted price
                </p>
              </div>

              <div className="tw-flex tw-flex-col tw-gap-2">
                <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                  Cost Price ({salesInfoData.currency || 'IDR'}) <span className="tw-text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={salesInfoData.costPrice || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "costPrice",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  placeholder="80000"
                  min={0}
                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                />

                {/* Cost Price Error Display */}
                {getFieldError("costPrice") && (
                  <div
                    className="tw-text-sm tw-text-red-600 dark:tw-text-red-400 tw-mt-1"
                    role="alert"
                  >
                    {getFieldError("costPrice")}
                  </div>
                )}

                {!getFieldError("costPrice") && (
                  <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                    Product cost for margin calculation
                  </p>
                )}
              </div>
            </div>
            </div>
          )}

          {/* Inventory Management (shown when no variants) */}
          {!salesInfoData.hasVariants && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                Stock Quantity <span className="tw-text-red-500">*</span>
              </label>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-gap-2">
                  <input
                    type="number"
                    value={salesInfoData.totalStock || ""}
                    onChange={(e) =>
                      handleTotalStockChange(parseInt(e.target.value) || 0)
                    }
                    placeholder="0"
                    min={0}
                    className="tw-flex-1 tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all tw-bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedNonVariantStock(!expandedNonVariantStock)
                    }
                    className="tw-px-3 tw-py-2.5 tw-bg-gray-100 dark:tw-bg-[#364152] tw-text-gray-700 dark:tw-text-gray-300 tw-rounded-lg hover:tw-bg-gray-200 dark:hover:tw-bg-[#4a5568] tw-transition-colors"
                  >
                    <span
                      className={classNames(
                        "material-symbols-outlined tw-text-[18px] tw-transition-transform",
                        expandedNonVariantStock ? "tw-rotate-180" : ""
                      )}
                    >
                      expand_more
                    </span>
                  </button>
                </div>

                {/* Expanded warehouse stock inputs */}
                {expandedNonVariantStock && (
                  <div className="tw-space-y-3 tw-pl-4 tw-border-l-2 tw-border-gray-200 dark:tw-border-[#364152]">
                    <h4 className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200 tw-mb-3">
                      Stock Distribution by Warehouse
                    </h4>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                      {WAREHOUSES.map((warehouse) => (
                        <div
                          key={warehouse.id}
                          className="tw-flex tw-flex-col tw-gap-2"
                        >
                          <label className="tw-text-xs tw-font-medium tw-text-gray-500 dark:tw-text-gray-400">
                            {warehouse.name}
                          </label>
                          <input
                            type="number"
                            value={
                              salesInfoData.warehouseStock?.[warehouse.code] ||
                              ""
                            }
                            onChange={(e) =>
                              handleWarehouseStockChange(
                                warehouse.code,
                                parseInt(e.target.value) || 0
                              )
                            }
                            placeholder="0"
                            min={0}
                            className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded tw-px-3 tw-py-2 tw-text-sm focus:tw-ring-1 focus:tw-ring-primary tw-text-gray-900 dark:tw-text-white"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-space-y-1">
                  <p>
                    Enter total stock quantity. Stock will be distributed equally across warehouses. Click expand to adjust individual warehouse quantities.
                  </p>
                  {salesInfoData.totalStock > 0 && (
                    <div className="tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-rounded tw-px-2 tw-py-1 tw-text-blue-700 dark:tw-text-blue-300">
                      <span className="tw-font-medium">Distribution:</span> 
                      {(() => {
                        const total = salesInfoData.totalStock;
                        const baseAmount = Math.floor(total / 3);
                        const remainder = total % 3;
                        const warehouseA = baseAmount + (remainder >= 1 ? 1 : 0);
                        const warehouseB = baseAmount + (remainder >= 2 ? 1 : 0);
                        const warehouseC = baseAmount;
                        return ` A: ${warehouseA}, B: ${warehouseB}, C: ${warehouseC}`;
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Variant Management (shown when has variants) */}
          {salesInfoData.hasVariants && (
            <div className="tw-space-y-6">
              {/* Variant Options Section */}
              <div>
                <div className="tw-mb-6 tw-flex tw-justify-between tw-items-start">
                  <div>
                    <h4 className="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white">
                      Variant Options
                    </h4>
                    <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
                      Add different options like color, size, material, etc.
                    </p>
                  </div>
                  <button
                    onClick={addVariantOption}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-primary hover:tw-text-primary/80 tw-transition-colors"
                  >
                    <span className="material-symbols-outlined tw-text-[20px]">
                      add_circle
                    </span>
                    Add Option
                  </button>
                </div>

                {/* Variant Options Error Display */}
                {getFieldError("variantOptions") && (
                  <div
                    className="tw-text-sm tw-text-red-600 dark:tw-text-red-400 tw-mb-4"
                    role="alert"
                  >
                    {getFieldError("variantOptions")}
                  </div>
                )}

                {/* Variant Options List */}
                <div className="tw-flex tw-flex-col tw-gap-6 tw-mb-6">
                  {(() => {
                    const options = salesInfoData.variantOptions || [];
                    return options.map((option, optionIndex) => {
                      return (
                        <div
                          key={option.id}
                          className="tw-p-4 sm:tw-p-5 tw-rounded-lg tw-bg-gray-50 dark:tw-bg-[#151f28] tw-border tw-border-gray-200 dark:tw-border-[#364152]"
                        >
                          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between sm:tw-items-center tw-gap-4 tw-mb-4">
                            <div className="tw-flex tw-items-center tw-gap-3">
                              <span className="material-symbols-outlined tw-text-gray-400 tw-text-[20px] tw-cursor-move">
                                drag_indicator
                              </span>
                              <div className="tw-flex tw-flex-col tw-flex-1">
                                <label className="tw-text-xs tw-font-medium tw-text-gray-600 dark:tw-text-gray-400 tw-mb-1">
                                  Option Type
                                </label>
                                <div className="tw-relative">
                                  <input
                                    type="text"
                                    value={option.name}
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      updateVariantOption(
                                        optionIndex,
                                        "name",
                                        newValue
                                      );
                                      setOptionDropdownOpen((prev) => ({
                                        ...prev,
                                        [optionIndex]: true,
                                      }));
                                    }}
                                    onClick={() =>
                                      setOptionDropdownOpen((prev) => ({
                                        ...prev,
                                        [optionIndex]: true,
                                      }))
                                    }
                                    placeholder="Click to select or enter custom option..."
                                    className="tw-w-full tw-bg-white dark:tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-cursor-pointer"
                                  />
                                  <span className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 material-symbols-outlined tw-text-gray-400 tw-text-[16px] tw-pointer-events-none">
                                    expand_more
                                  </span>

                                  {/* Option Type Suggestions */}
                                  {optionDropdownOpen[optionIndex] && (
                                    <div
                                      className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-shadow-lg tw-z-20 tw-max-h-48 tw-overflow-y-auto"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="tw-p-2 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
                                        Common options or type your own:
                                      </div>
                                      <div className="tw-py-1">
                                        {[
                                          "Color",
                                          "Size",
                                          "Material",
                                          "Style",
                                          "Pattern",
                                          "Flavor",
                                          "Weight",
                                          "Length",
                                          "Width",
                                          "Capacity",
                                        ].map((optionType) => (
                                          <button
                                            key={optionType}
                                            onClick={() => {
                                              updateVariantOption(
                                                optionIndex,
                                                "name",
                                                optionType
                                              );
                                              setOptionDropdownOpen((prev) => ({
                                                ...prev,
                                                [optionIndex]: false,
                                              }));
                                            }}
                                            className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-text-gray-900 dark:tw-text-white tw-flex tw-items-center tw-gap-2"
                                          >
                                            {optionType}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <span className="tw-text-[10px] tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
                                  Option Type
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => removeVariantOption(optionIndex)}
                              className="tw-text-gray-400 hover:tw-text-red-500 tw-transition-colors"
                            >
                              <span className="material-symbols-outlined tw-text-[20px]">
                                delete
                              </span>
                            </button>
                          </div>

                          {/* Values for this option */}
                          <div>
                            <p className="tw-text-xs tw-text-gray-500 tw-mb-2">
                              Add values for this option. Click suggestions or
                              type your own.
                            </p>

                            {/* Tag-style value display */}
                            {option.values.length > 0 && (
                              <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-3">
                                {option.values.map((value, valueIndex) => (
                                  <div
                                    key={valueIndex}
                                    className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-primary/10 tw-text-primary tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-border tw-border-primary/20"
                                  >
                                    {/* Show color preview for first option with image/color */}
                                    {optionIndex === 0 && value.image && (
                                      <div
                                        className="tw-w-3 tw-h-3 tw-rounded-full tw-border tw-border-white/50"
                                        style={{ backgroundColor: value.image }}
                                      />
                                    )}
                                    <span>{value.name}</span>
                                    <button
                                      onClick={() =>
                                        removeVariantValue(
                                          optionIndex,
                                          valueIndex
                                        )
                                      }
                                      className="tw-text-primary/70 hover:tw-text-primary tw-transition-colors"
                                    >
                                      <span className="material-symbols-outlined tw-text-[14px]">
                                        close
                                      </span>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Value input with suggestions - matching HTML design exactly */}
                            <VariantValueInput
                              optionIndex={optionIndex}
                              option={option}
                              onAddValue={addOptionValue}
                              getVariantSuggestions={getVariantSuggestions}
                            />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Variant Combinations Section */}
                {(salesInfoData.variantCombinations || []).length > 0 && (
                  <div>
                    <h4 className="tw-text-base tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-4">
                      Variant Combinations (
                      {(salesInfoData.variantCombinations || []).length})
                    </h4>

                    {/* Mobile Variant Cards */}
                    <div className="md:tw-hidden tw-space-y-4">
                      {(salesInfoData.variantCombinations || []).map(
                        (combination, index) => (
                          <div
                            key={combination.id}
                            className="tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-xl tw-overflow-hidden hover:tw-border-gray-300 dark:hover:tw-border-[#4a5568] tw-transition-all"
                          >
                            <div className="tw-p-4 tw-border-b tw-border-gray-200 dark:tw-border-[#364152] tw-bg-gray-50 dark:tw-bg-[#151f28]">
                              <div className="tw-flex tw-items-center tw-gap-3">
                                <div className="tw-flex-shrink-0">
                                  {/* Mobile Image Upload with Preview */}
                                  <div className="tw-relative tw-group">
                                    {!combination.image ? (
                                      <div className="tw-w-16 tw-h-16 tw-border-2 tw-border-dashed tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-border-primary hover:tw-bg-primary/5 tw-transition-all tw-touch-manipulation">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="tw-absolute tw-inset-0 tw-opacity-0 tw-cursor-pointer"
                                          onChange={(e) =>
                                            handleVariantImageUpload(e, index)
                                          }
                                        />
                                        <span className="material-symbols-outlined tw-text-gray-400 tw-text-[20px]">
                                          add_photo_alternate
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="tw-relative tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-overflow-hidden">
                                        <img
                                          src={combination.image}
                                          className="tw-w-full tw-h-full tw-object-cover"
                                          alt={combination.name}
                                        />
                                        <div className="tw-absolute tw-inset-0 tw-bg-black/40 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center">
                                          <button
                                            onClick={() =>
                                              updateVariantCombination(
                                                index,
                                                "image",
                                                null
                                              )
                                            }
                                            className="tw-w-8 tw-h-8 tw-bg-red-500/80 hover:tw-bg-red-600 tw-rounded-full tw-text-white tw-transition-colors tw-flex tw-items-center tw-justify-center tw-touch-manipulation"
                                            type="button"
                                          >
                                            <span className="material-symbols-outlined tw-text-[16px]">
                                              close
                                            </span>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="tw-flex-1 tw-min-w-0">
                                  <h5 className="tw-text-lg tw-font-semibold tw-text-gray-900 dark:tw-text-white tw-mb-1">
                                    {combination.name}
                                  </h5>
                                  <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                                    Combination #{index + 1}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Mobile Card Content */}
                            <div className="tw-p-4 tw-space-y-4">
                              {/* Pricing Section */}
                              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                <div className="tw-space-y-2">
                                  <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                                    Price{" "}
                                    <span className="tw-text-red-500">*</span>
                                  </label>
                                  <div className="tw-relative">
                                    <span className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500 tw-text-sm">
                                      {salesInfoData.currency || 'IDR'}
                                    </span>
                                    <input
                                      type="number"
                                      value={combination.price || ""}
                                      onChange={(e) =>
                                        updateVariantCombination(
                                          index,
                                          "price",
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      placeholder="0"
                                      className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-pl-10 tw-pr-4 tw-py-3 tw-min-h-[48px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all tw-touch-manipulation"
                                    />
                                  </div>
                                </div>
                                <div className="tw-space-y-2">
                                  <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                                    Cost
                                  </label>
                                  <div className="tw-relative">
                                    <span className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500 tw-text-sm">
                                      {salesInfoData.currency || 'IDR'}
                                    </span>
                                    <input
                                      type="number"
                                      value={combination.cost || ""}
                                      onChange={(e) =>
                                        updateVariantCombination(
                                          index,
                                          "cost",
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      placeholder="0"
                                      className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-pl-10 tw-pr-4 tw-py-3 tw-min-h-[48px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all tw-touch-manipulation"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Stock Management Section - With Warehouse Expansion */}
                              <div className="tw-space-y-3">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                  <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                                    Warehouse Stock
                                  </label>
                                  <button
                                    onClick={() =>
                                      setVariantStockExpanded((prev) => ({
                                        ...prev,
                                        [index]: !prev[index],
                                      }))
                                    }
                                    className="tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-primary hover:tw-text-primary/80 tw-transition-colors tw-touch-manipulation"
                                  >
                                    <span>
                                      {variantStockExpanded[index]
                                        ? "Collapse"
                                        : "Expand"}
                                    </span>
                                    <span
                                      className={classNames(
                                        "material-symbols-outlined tw-text-[18px] tw-transition-transform",
                                        variantStockExpanded[index]
                                          ? "tw-rotate-180"
                                          : ""
                                      )}
                                    >
                                      expand_more
                                    </span>
                                  </button>
                                </div>

                                {/* Total Stock Display */}
                                <div className="tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-rounded-lg tw-px-4 tw-py-3">
                                  <div className="tw-flex tw-justify-between tw-items-center">
                                    <span className="tw-text-sm tw-font-medium tw-text-blue-800 dark:tw-text-blue-200">
                                      Total Stock:
                                    </span>
                                    <span className="tw-text-lg tw-font-bold tw-text-blue-900 dark:tw-text-blue-100">
                                      {combination.stock || 0}
                                    </span>
                                  </div>
                                </div>

                                {/* Expandable Warehouse Stock Inputs */}
                                {variantStockExpanded[index] && (
                                  <div className="tw-space-y-3">
                                    {WAREHOUSES.map((warehouse) => (
                                      <div
                                        key={warehouse.id}
                                        className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 dark:tw-bg-gray-800/30 tw-rounded-lg tw-px-4 tw-py-3"
                                      >
                                        <div className="tw-flex-1">
                                          <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
                                            {warehouse.name}
                                          </span>
                                        </div>
                                        <div className="tw-flex-shrink-0 tw-ml-3">
                                          <input
                                            type="number"
                                            placeholder="0"
                                            value={
                                              combination.warehouseStock?.[
                                                warehouse.code
                                              ] || ""
                                            }
                                            onChange={(e) =>
                                              updateVariantWarehouseStock(
                                                index,
                                                warehouse.code,
                                                parseInt(e.target.value) || 0
                                              )
                                            }
                                            className="tw-w-20 tw-bg-white dark:tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-text-center focus:tw-ring-2 focus:tw-ring-primary tw-text-gray-900 dark:tw-text-white tw-touch-manipulation"
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* SKU Section */}
                              <div className="tw-space-y-2">
                                <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                                  SKU <span className="tw-text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  value={combination.sku || ""}
                                  onChange={(e) =>
                                    updateVariantCombination(
                                      index,
                                      "sku",
                                      e.target.value
                                    )
                                  }
                                  placeholder={`VARIANT-${index + 1}`}
                                  className="tw-w-full tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-3 tw-min-h-[48px] tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all tw-touch-manipulation"
                                />
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>

                    {/* Desktop Variant Table */}
                    <div className="tw-hidden md:tw-block tw-overflow-x-auto tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152]">
                      <table className="tw-w-full tw-text-left tw-text-sm tw-whitespace-nowrap">
                        <thead className="tw-bg-gray-50 dark:tw-bg-[#233648] tw-text-gray-500 dark:tw-text-gray-300">
                          <tr>
                            <th className="tw-px-4 tw-py-3 tw-font-medium">
                              Image
                            </th>
                            <th className="tw-px-4 tw-py-3 tw-font-medium">
                              Variant Combination
                            </th>
                            <th className="tw-px-4 tw-py-3 tw-font-medium">
                              Price ({salesInfoData.currency || 'IDR'})
                            </th>
                            <th className="tw-px-4 tw-py-3 tw-font-medium">
                              Cost ({salesInfoData.currency || 'IDR'})
                            </th>
                            <th className="tw-px-4 tw-py-3 tw-font-medium">
                              Stock
                            </th>
                            <th className="tw-px-4 tw-py-3 tw-font-medium">
                              SKU
                            </th>
                          </tr>
                        </thead>
                        <tbody className="tw-bg-white dark:tw-bg-[#1C252E]">
                          {(salesInfoData.variantCombinations || []).map(
                            (combination, index) => (
                              <React.Fragment key={combination.id}>
                                <tr className="hover:tw-bg-gray-50 dark:hover:tw-bg-[#233648] tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
                                  <td className="tw-px-4 tw-py-3">
                                    <div className="tw-relative tw-group">
                                      {!combination.image ? (
                                        <div className="tw-w-16 tw-h-16 tw-border-2 tw-border-dashed tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-border-primary hover:tw-bg-primary/5 tw-transition-all">
                                          <input
                                            type="file"
                                            accept="image/*"
                                            className="tw-absolute tw-inset-0 tw-opacity-0 tw-cursor-pointer"
                                            onChange={(e) =>
                                              handleVariantImageUpload(e, index)
                                            }
                                          />
                                          <span className="material-symbols-outlined tw-text-gray-400 tw-text-[20px]">
                                            add_photo_alternate
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="tw-relative tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-overflow-hidden">
                                          <img
                                            src={combination.image}
                                            className="tw-w-full tw-h-full tw-object-cover"
                                            alt={combination.name}
                                          />
                                          <div className="tw-absolute tw-inset-0 tw-bg-black/40 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-flex tw-items-center tw-justify-center">
                                            <button
                                              onClick={() =>
                                                updateVariantCombination(
                                                  index,
                                                  "image",
                                                  null
                                                )
                                              }
                                              className="tw-p-1 tw-bg-red-500/80 hover:tw-bg-red-600 tw-rounded tw-text-white tw-transition-colors"
                                              type="button"
                                            >
                                              <span className="material-symbols-outlined tw-text-[14px]">
                                                close
                                              </span>
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="tw-px-4 tw-py-3">
                                    <span className="tw-font-medium tw-text-gray-900 dark:tw-text-white">
                                      {combination.name}
                                    </span>
                                  </td>
                                  <td className="tw-px-4 tw-py-3">
                                    <input
                                      type="number"
                                      value={combination.price || ""}
                                      onChange={(e) =>
                                        updateVariantCombination(
                                          index,
                                          "price",
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      placeholder="0"
                                      className="tw-w-24 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded tw-px-2 tw-py-1 tw-text-sm focus:tw-ring-1 focus:tw-ring-primary tw-text-gray-900 dark:tw-text-white"
                                    />
                                  </td>
                                  <td className="tw-px-4 tw-py-3">
                                    <input
                                      type="number"
                                      value={combination.cost || ""}
                                      onChange={(e) =>
                                        updateVariantCombination(
                                          index,
                                          "cost",
                                          parseFloat(e.target.value) || 0
                                        )
                                      }
                                      placeholder="0"
                                      className="tw-w-24 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded tw-px-2 tw-py-1 tw-text-sm focus:tw-ring-1 focus:tw-ring-primary tw-text-gray-900 dark:tw-text-white"
                                    />
                                  </td>
                                  <td className="tw-px-4 tw-py-3">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                      <input
                                        type="number"
                                        value={combination.stock || ""}
                                        readOnly
                                        placeholder="0"
                                        className="tw-w-16 tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-text-gray-900 dark:tw-text-white tw-bg-gray-50 dark:tw-bg-gray-800 tw-cursor-not-allowed"
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setVariantStockExpanded((prev) => ({
                                            ...prev,
                                            [index]: !prev[index],
                                          }))
                                        }
                                        className="tw-p-1 tw-bg-gray-100 dark:tw-bg-[#364152] tw-text-gray-700 dark:tw-text-gray-300 tw-rounded hover:tw-bg-gray-200 dark:hover:tw-bg-[#4a5568] tw-transition-colors"
                                        title={
                                          variantStockExpanded[index]
                                            ? "Hide warehouse details"
                                            : "Show warehouse details"
                                        }
                                      >
                                        <span
                                          className={classNames(
                                            "material-symbols-outlined tw-text-[16px] tw-transition-transform",
                                            variantStockExpanded[index]
                                              ? "tw-rotate-180"
                                              : ""
                                          )}
                                        >
                                          expand_more
                                        </span>
                                      </button>
                                    </div>
                                  </td>
                                  <td className="tw-px-4 tw-py-3">
                                    <input
                                      type="text"
                                      value={combination.sku || ""}
                                      onChange={(e) =>
                                        updateVariantCombination(
                                          index,
                                          "sku",
                                          e.target.value
                                        )
                                      }
                                      placeholder={`SKU-${index + 1}`}
                                      className="tw-w-32 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded tw-px-2 tw-py-1 tw-text-sm focus:tw-ring-1 focus:tw-ring-primary tw-text-gray-900 dark:tw-text-white"
                                    />
                                  </td>
                                </tr>

                                {/* Expandable Warehouse Stock Row */}
                                {variantStockExpanded[index] && (
                                  <tr className="tw-bg-gray-50 dark:tw-bg-[#1C252E]/50">
                                    <td colSpan={6} className="tw-p-0">
                                      <div className="tw-px-8 tw-py-4">
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                                          {WAREHOUSES.map((warehouse) => (
                                            <div
                                              key={warehouse.id}
                                              className="tw-bg-white dark:tw-bg-[#233648] tw-rounded-lg tw-p-3 tw-border tw-border-gray-200 dark:tw-border-[#364152]"
                                            >
                                              <div className="tw-flex tw-justify-between tw-items-center">
                                                <div>
                                                  <p className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300">
                                                    {warehouse.name}
                                                  </p>
                                                  <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                                                    {warehouse.location}
                                                  </p>
                                                </div>
                                                <input
                                                  type="number"
                                                  value={
                                                    combination
                                                      .warehouseStock?.[
                                                      warehouse.code
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    updateVariantWarehouseStock(
                                                      index,
                                                      warehouse.code,
                                                      parseInt(
                                                        e.target.value
                                                      ) || 0
                                                    )
                                                  }
                                                  placeholder="0"
                                                  min="0"
                                                  className="tw-w-20 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-text-center focus:tw-ring-1 focus:tw-ring-primary tw-text-gray-900 dark:tw-text-white"
                                                />
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                        <div className="tw-mt-3 tw-flex tw-justify-end">
                                          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                                            Total Stock:{" "}
                                            <span className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">
                                              {combination.stock || 0}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Tags & Promotion */}
      <div className="tw-bg-surface-light dark:tw-bg-surface-dark tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Product Tags & Promotion
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Add searchable tags and promotional information to help customers
            find your product.
          </p>
        </div>

        <div className="tw-grid tw-gap-6">
          {/* Search Tags */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
              Product Tags
            </label>
            <div className="tw-flex tw-flex-col tw-gap-3">
              {/* Tag Input */}
              <div className="tw-flex tw-gap-2">
                <input
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyUp={(e) => e.key === "Enter" && addTag()}
                  className="tw-flex-1 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="Enter a tag and press Enter"
                  type="text"
                />
                <button
                  onClick={addTag}
                  className="tw-px-4 tw-py-2 tw-bg-primary tw-text-white tw-rounded-lg hover:tw-bg-primary/90 tw-transition-colors tw-flex tw-items-center tw-gap-2"
                >
                  <span className="material-symbols-outlined tw-text-[16px]">
                    add
                  </span>
                  Add
                </button>
              </div>

              {/* Current Tags */}
              {(salesInfoData.tags || []).length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {(salesInfoData.tags || []).map((tag, index) => (
                    <span
                      key={index}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-bg-blue-100 dark:tw-bg-blue-900/30 tw-text-blue-800 dark:tw-text-blue-300 tw-rounded-full tw-text-sm"
                    >
                      <span>{tag}</span>
                      <button
                        onClick={() => removeTag(index)}
                        className="tw-text-blue-600 dark:tw-text-blue-400 hover:tw-text-blue-800 dark:hover:tw-text-blue-200"
                      >
                        <span className="material-symbols-outlined tw-text-[14px]">
                          close
                        </span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Add tags to help customers find your product. Press Enter or click
              Add to add tags.
            </p>
          </div>

          {/* Promotion Tags */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
              Promotion Tags
            </label>
            <div className="tw-flex tw-flex-col tw-gap-3">
              {/* Promotion Tag Input */}
              <div className="tw-flex tw-gap-2">
                <input
                  value={currentPromotionTag}
                  onChange={(e) => setCurrentPromotionTag(e.target.value)}
                  onKeyUp={(e) => e.key === "Enter" && addPromotionTag()}
                  className="tw-flex-1 tw-bg-transparent tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-gray-900 dark:tw-text-white tw-placeholder-gray-400 dark:tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-transparent tw-transition-all"
                  placeholder="Enter promotion tag and press Enter"
                  type="text"
                />
                <button
                  onClick={addPromotionTag}
                  className="tw-px-4 tw-py-2 tw-bg-primary tw-text-white tw-rounded-lg hover:tw-bg-primary/90 tw-transition-colors tw-flex tw-items-center tw-gap-2"
                >
                  <span className="material-symbols-outlined tw-text-[16px]">
                    add
                  </span>
                  Add
                </button>
              </div>

              {/* Current Promotion Tags */}
              {(salesInfoData.promotionTags || []).length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {(salesInfoData.promotionTags || []).map((tag, index) => (
                    <span
                      key={index}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-bg-blue-100 dark:tw-bg-blue-900/30 tw-text-blue-800 dark:tw-text-blue-300 tw-rounded-full tw-text-sm"
                    >
                      <span>{tag}</span>
                      <button
                        onClick={() => removePromotionTag(index)}
                        className="tw-text-blue-600 dark:tw-text-blue-400 hover:tw-text-blue-800 dark:hover:tw-text-blue-200"
                      >
                        <span className="material-symbols-outlined tw-text-[14px]">
                          close
                        </span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
              Add promotional tags to highlight special offers or product
              features.
            </p>
          </div>
        </div>
      </div>

      {/* Step Errors Display */}
      {errors.length > 0 && (
        <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-start tw-gap-3">
            <span className="material-symbols-outlined tw-text-red-600 dark:tw-text-red-400 tw-mt-0.5">
              error
            </span>
            <div>
              <h4 className="tw-font-medium tw-text-red-800 dark:tw-text-red-300 tw-mb-2">
                Please correct the following errors:
              </h4>
              <ul className="tw-text-sm tw-text-red-700 dark:tw-text-red-400 tw-space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesInfoStep;
